<template>
  <div :class="[$tvaMq]" :version="version.version">
    <teleport to="head"
      ><title>{{ metaContent.title }}</title>
      <meta name="description" :content="metaContent.description" />
      <!-- FACEBOOK -->
      <meta property="og:title" :content="metaContent.title" />
      <meta property="og:description" :content="metaContent.description" />
      <meta property="og:type" content="website" />
      <meta
        property="og:image"
        :content="`www.eni.com/visual-design/infographics/longform-enistoria/social/${metaContent.image}.jpg`"
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <!-- TWITTER -->
      <meta name="twitter:site" :content="metaContent.title" />
      <meta name="twitter:title" :content="metaContent.title" />
      <meta name="twitter:description" :content="metaContent.description" />
      <meta
        name="twitter:image"
        :content="`www.eni.com/visual-design/infographics/longform-enistoria/social/${metaContent.image}.jpg`"
      />
      /></teleport
    >
    <router-view></router-view>
  </div>
</template>

<script setup>
import version from "@/../package.json";
import { onMounted, provide, watch } from "@vue/runtime-core";
import useTvaMq from "./plugins/tvaMq.js";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useUiStore } from "./utilities/stores/ui";
import { useDataStore } from "./utilities/stores/data";
import { deepMap } from "./utilities/functions/deepMap";
import { computed } from "@vue/reactivity";

const { $tvaMq } = useTvaMq();
const route = useRoute();
const router = useRouter();
const i18n = useI18n();

const { tm, rt } = useI18n();

const metaContent = computed(() => deepMap(tm("header.socialSharing"), rt));

const { setOptions } = useUiStore();
const { loadData, loadYear } = useDataStore();

provide("$tvaMq", $tvaMq);
provide("version", version);

// Calling this here is equivalent to calling it in
// beforeCreated / create in Options API
setLanguage();

onMounted(() => {
  setVHProperty();
  if (route.params.lang) {
    import(`./assets/data/dataset${route.params.lang}.json`).then((res) =>
      loadData(res.default)
    );
  }

  window.addEventListener("resize", setVHProperty);
  window.addEventListener("orientationchange", setVHProperty);
});

watch(
  () => route.params.lang,
  () => {
    if (route.params.lang) {
      import(`./assets/data/dataset${route.params.lang}.json`).then((res) => {
        loadData(res.default);
        if (route.query.year) loadYear(route.query.year);
      });
      i18n.locale.value = route.params.lang.toLowerCase();
      setLanguage();
    }
  }
);

watch(
  () => route.query,
  () => {
    if (!route.query.disposition) {
      router.push({
        path: "/",
        query: { ...route.query, disposition: "grid" },
      });
    }
    setOptions(route.query);
    if (route.query.year)
      import(`./assets/data/dataset${route.params.lang}.json`)
        .then((res) => loadData(res.default))
        .then(() => loadYear(route.query.year));
  }
);

function setLanguage() {
  const defaultLang = "it";
  let lang;
  const routerLang = route && route.params ? route.params.lang : null;
  let langFromOutside =
    routerLang ||
    process.env.VUE_APP_LANG ||
    document.querySelector("#app").getAttribute("lang");

  if (langFromOutside) {
    lang = langFromOutside;
  } else {
    lang = defaultLang;
    route.params.lang = defaultLang;
  }

  i18n.locale.value = lang.toLowerCase();

  // Set html language attribute
  const htmlEl = document.querySelector("html");
  if (htmlEl) {
    htmlEl.setAttribute("lang", lang.toLowerCase());
  }
  window.digitalData[0].page.pageInfo.pageLanguage =
    lang.toLocaleLowerCase() === "it" ? "it-IT" : "en-IT";
}

function setVHProperty() {
  if ($tvaMq.value === "mobile") {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  }
}
</script>

<style lang="scss">
html {
  width: 100%;
  height: 100%;
  scroll-behavior: smooth;
}

body {
  width: 100%;
  height: 100%;
  #onetrust-consent-sdk {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji",
      "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: #333;
    text-align: left;
    background-color: #fff;
  }
}

#app {
  width: 100%;
  height: 100%;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
}
</style>
